import { houseTemplate } from "./chart-theme";

type Figure = { data: unknown[]; layout?: Record<string, unknown> };

// Replace each server-rendered chart fallback with an interactive Plotly chart
// when it comes near the viewport. Plotly (about 1 MB) loads only on pages that
// have a chart, and only once.
export function mountCharts(root: HTMLElement) {
  const targets = Array.from(
    root.querySelectorAll<HTMLElement>(
      "[data-chart-src]:not([data-chart-mounted])",
    ),
  );
  if (!targets.length) return () => {};
  let cancelled = false;
  const mounted: HTMLElement[] = [];

  async function mount(target: HTMLElement) {
    target.dataset.chartMounted = "pending";
    try {
      const [{ default: Plotly }, response] = await Promise.all([
        import("plotly.js-cartesian-dist-min"),
        fetch(target.dataset.chartSrc!),
      ]);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const figure = (await response.json()) as Figure;
      if (cancelled) return;
      const plot = document.createElement("div");
      plot.className = "blog-chart-canvas";
      target.replaceChildren(plot);
      target.setAttribute("role", "img");
      target.setAttribute("aria-label", target.dataset.chartAlt || "Chart");
      const layout: Record<string, unknown> = {
        ...figure.layout,
        autosize: true,
        width: undefined,
        height: undefined,
      };
      if (!layout.template) layout.template = houseTemplate;
      await Plotly.newPlot(plot, figure.data, layout, {
        responsive: true,
        displaylogo: false,
        modeBarButtonsToRemove: ["select2d", "lasso2d", "autoScale2d"],
      });
      mounted.push(plot);
      target.dataset.chartMounted = "true";
    } catch (error) {
      // Keep the static image; it already carries the same chart.
      target.dataset.chartMounted = "failed";
      console.warn("Chart did not load", target.dataset.chartSrc, error);
    }
  }

  let observer: IntersectionObserver | undefined;
  if ("IntersectionObserver" in window) {
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          observer!.unobserve(entry.target);
          void mount(entry.target as HTMLElement);
        }
      },
      { rootMargin: "600px 0px" },
    );
    targets.forEach((target) => observer!.observe(target));
  } else {
    targets.forEach((target) => void mount(target));
  }

  return () => {
    cancelled = true;
    observer?.disconnect();
    void import("plotly.js-cartesian-dist-min").then(({ default: Plotly }) =>
      mounted.forEach((plot) => Plotly.purge(plot)),
    );
  };
}
